import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import { uploadCsvCandidateFile } from "../../Redux/ActionCreators/candidateAction";
import { FileTypeIcon, parseCsvText, AlreadyAppliedCheckbox } from "./sharedUI";

const UploadCsvPanel = ({ jobId, onInvited }) => {
    const dispatch = useDispatch();

    const [csvFile, setCsvFile] = useState(null);
    const [previewRows, setPreviewRows] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [alreadyApplied, setAlreadyApplied] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const loadFile = (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".csv")) {
            setSnackbar({ open: true, message: "Please select a .csv file.", severity: "error" });
            return;
        }
        setCsvFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            const { rows } = parseCsvText(String(e.target?.result || ""));
            setPreviewRows(rows.slice(0, 5));
        };
        reader.readAsText(file);
    };

    const handleChooseFile = (e) => {
        const file = e.target.files?.[0];
        if (file) loadFile(file);
        e.target.value = null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current += 1;
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); }
    };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) loadFile(file);
    };

    const removeFile = () => {
        setCsvFile(null);
        setPreviewRows([]);
    };

    const downloadSampleCsv = () => {
        const headers = ["name", "email", "phonenumber"];
        const csvContent = [headers].map((r) => r.join(",")).join("\n");
        const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", dataUri);
        link.setAttribute("download", "sample.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleAddToInviteList = async () => {
        if (!csvFile || uploading) return;
        setUploading(true);
        try {
            const result = await dispatch(uploadCsvCandidateFile(jobId, csvFile, alreadyApplied));
            if (result?.success) {
                // already_applied=true → candidates go straight to the job, not
                // the invite list, so only refetch when an invite flow ran.
                if (!alreadyApplied) onInvited?.();

                const skipped = Array.isArray(result.data?.skipped) ? result.data.skipped : [];
                const baseMessage = result.message || (alreadyApplied
                    ? "CSV uploaded. Candidates added to the job."
                    : "CSV uploaded. Invites are being sent.");

                if (skipped.length > 0) {
                    const added = (result.data?.invites_created || 0) + (result.data?.emails_queued || 0);
                    const alreadyInJob = skipped.filter((s) => s.reason === "already_in_job").length;
                    const detail = alreadyInJob > 0 ? ` (${alreadyInJob} already in this job)` : "";
                    const skippedText = `${skipped.length} record${skipped.length > 1 ? "s" : ""} skipped${detail}.`;
                    setSnackbar({
                        open: true,
                        message: added > 0 ? `${added} added, ${skippedText}` : skippedText,
                        severity: "warning",
                    });
                } else {
                    setSnackbar({
                        open: true,
                        message: baseMessage,
                        severity: "success",
                    });
                }
                removeFile();
            } else {
                setSnackbar({ open: true, message: result?.message || "CSV upload failed", severity: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err?.message || "CSV upload failed", severity: "error" });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="icv2-upload-panel">
            <div className="icv2-upload-panel-head">
                <div className="icv2-upload-panel-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M9 15h6M9 11h6" />
                    </svg>
                </div>
                <div>
                    <h4>Bring your list with you</h4>
                    <p>Drop a CSV here or choose a file to preview the first rows before inviting.</p>
                </div>
                <button className="icv2-outline-btn" onClick={() => fileInputRef.current?.click()}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Choose file
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleChooseFile}
                />
            </div>

            {!csvFile ? (
                <div
                    className={`icv2-dropzone${isDragging ? " icv2-dropzone--dragging" : ""}`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#F75891" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 14.9A5 5 0 0 1 6 5.3a6 6 0 0 1 11.3-1A5 5 0 0 1 18 14.9" />
                        <path d="M12 12v9" />
                        <path d="m8 16 4-4 4 4" />
                    </svg>
                    <p className="icv2-dropzone-title">Drop your CSV here</p>
                    <p className="icv2-dropzone-sub">or choose a file from your computer</p>
                </div>
            ) : (
                <div className="icv2-file-preview">
                    <div className="icv2-file-row">
                        <FileTypeIcon name={csvFile.name} />
                        <div className="icv2-file-row-info">
                            <p title={csvFile.name}>{csvFile.name}</p>
                            <span>{(csvFile.size / 1024).toFixed(1)} KB{previewRows.length > 0 ? ` · ${previewRows.length} row${previewRows.length > 1 ? "s" : ""} previewed` : ""}</span>
                        </div>
                        <button className="icv2-remove-x" onClick={removeFile} title="Remove">×</button>
                    </div>

                    {previewRows.length > 0 && (
                        <div className="icv2-csv-preview-table-wrap">
                            <table className="icv2-csv-preview-table">
                                <thead>
                                    <tr>{Object.keys(previewRows[0]).map((h) => <th key={h}>{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {previewRows.map((row, i) => (
                                        <tr key={i}>{Object.keys(previewRows[0]).map((h) => <td key={h}>{row[h] || "—"}</td>)}</tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="icv2-upload-panel-footer">
                <div className="icv2-expected-cols">
                    <span className="icv2-expected-cols-label">Expected columns</span>
                    <span className="icv2-expected-cols-value">name, email, phonenumber</span>
                </div>
                <button className="icv2-link-btn icv2-link-btn--icon" onClick={downloadSampleCsv}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download template
                </button>
            </div>

            <AlreadyAppliedCheckbox
                checked={alreadyApplied}
                onChange={setAlreadyApplied}
                disabled={uploading}
            />

            <button
                className="icv2-primary-btn icv2-primary-btn--block"
                onClick={handleAddToInviteList}
                disabled={!csvFile || uploading}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" y1="8" x2="19" y2="14" />
                    <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
                {uploading
                    ? "Uploading…"
                    : alreadyApplied
                        ? "Add candidates to job"
                        : "Upload CSV & send invites"}
            </button>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default UploadCsvPanel;
